import { createSelector } from "@reduxjs/toolkit";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import companyToken from "Utils/companyToken";
import { prepareHeadersWithCSRFToken } from "Utils/fetchWithCSRFToken";
import { convertDataToFormDataFormat } from "Utils/formDataHelpers";

import { appendAuthParams } from "Common/TokenAuth/Utils";

interface UploadFileArgs {
  file: File[];
}

export const largeGroupSurveyApi = createApi({
  reducerPath: "largeGroupSurveyApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "/api/v1/",
    prepareHeaders: prepareHeadersWithCSRFToken,
  }),

  endpoints: (builder) => ({
    getSurvey: builder.query({
      query: () => {
        const companyIDToken = companyToken();
        const data = {
          ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
        };

        const params = new URLSearchParams(data).toString();
        return appendAuthParams(`/register-large-group?${params}`);
      },
      providesTags: ["Application"],
    }),

    getCarrierOptions: builder.query({
      query: () => {
        const companyIDToken = companyToken();
        const data = {
          ...(companyIDToken ? { company_id_token: companyIDToken } : {}),
        };

        const params = new URLSearchParams(data).toString();
        return appendAuthParams(`/register-large-group/carrier-options?${params}`);
      },
      providesTags: ["Carriers"],
    }),

    updateLargeGroupSurvey: builder.mutation({
      query: (body) => ({
        url: appendAuthParams("/register-large-group"),
        method: "POST",
        body: {
          ...body,
          company_id_token: companyToken(),
        },
      }),

      invalidatesTags: ["Application"],
    }),

    uploadCensusFile: builder.mutation<{ success: true }, UploadFileArgs>({
      query: ({ files }) => ({
        url: appendAuthParams(`/register-large-group/upload-census-file`),
        method: "PATCH",
        body: convertDataToFormDataFormat({
          files,
          company_id_token: companyToken(),
        }),
      }),
      invalidatesTags: ["Census"],
    }),

    sendLargeGroupSurveyRequest: builder.mutation({
      query: () => ({
        url: appendAuthParams("/register-large-group/send-large-group-quote-request"),
        method: "POST",
        body: { company_id_token: companyToken() },
      }),

      invalidatesTags: ["Application"],
    }),
  }),

  tagTypes: ["Application", "Census", "Carriers"],
});

export const {
  useGetSurveyQuery,
  useUpdateLargeGroupSurveyMutation,
  useUploadCensusFileMutation,
  useSendLargeGroupSurveyRequestMutation,
  useGetCarrierOptionsQuery,
} = largeGroupSurveyApi;

const selectLargeGroupSurveyResult = largeGroupSurveyApi.endpoints.getSurvey.select();

export const getLargeGroupSurvey = createSelector(selectLargeGroupSurveyResult, (largeGroupSurveyResult) => largeGroupSurveyResult?.data);
